/* src/views/logements/useParcDraft.js — LA MÉCANIQUE DE BROUILLON de la
   destination ⌂ Logements (lot 4, 2026-08-28), extraite fidèlement de
   src/tabs/LogementsPanel.jsx (ADR-050/197, fixes B2/C-1/C-2).

   Décision Dimitri (lot 4, point 6) : le bouton « Enregistrer » RESTE —
   la moitié des champs de la fiche n'écrivent que ce brouillon local
   (fbz8-cfg via /api/cfg), le supprimer perdrait ces saisies en silence.
   La migration vers l'écriture immédiate est un chantier séparé.

   Deux régimes d'écriture cohabitent donc (documentés dans
   docs/handoff/INVENTAIRE_LOT4_LOGEMENTS.md §2.6) :
   - draft cfg : updateLog/updateProp/… + dirty → handleSave (💾)
   - immédiat  : applyPatchAndSaveNow (barème taxe, ville…) + les éditeurs
     DB V2 montés par les sous-onglets (prix, livret, matrice voyageur…). */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { propDisplayName } from '../../lib/format'
import { clearMappingCache } from '../../lib/beds24-mapping-lookup'
import { buildCopyPlan, applyCopyPlan } from '../../lib/copy-to-chambres'
import { construireProprio, raisonsNonSupprimable } from '../../lib/proprio-creation'

/* Un logement parent de sous-location (Maison Zen l8) n'est pas une feuille
   louable : simple ligne de compta, jamais ouvert dans la fiche (ADR-197). */
export function hasChambres(log) {
  return Array.isArray(log?.chambres) && log.chambres.length > 0
}

export function firstSelectableId(props) {
  for (const p of props || []) {
    for (const l of p.logs || []) {
      if (hasChambres(l)) {
        if (l.chambres[0]?.id) return l.chambres[0].id
        continue
      }
      if (l.id) return l.id
    }
  }
  return null
}

/* `p<n>` = max + 1 sur tout le parc. Hors du composant : il ne lit aucun
   état, et le serveur en garde le miroir (lib/proprietaire-creer-handlers.js). */
export function nextPropId(props) {
  const nums = (props || []).map((p) => {
    const m = String(p.id || '').match(/^p(\d+)$/)
    return m ? parseInt(m[1], 10) : 0
  })
  return 'p' + (Math.max(0, ...nums) + 1)
}

// `data`, `saveData`, `factLog`, `saveLog` sont encore passés par l'appelant
// mais ne sont plus lus : la suppression d'un logement n'efface plus ni les
// relevés ni le journal de facturation (voir removeLogConfirmed).
export default function useParcDraft({ cfg, save }) {
  const [c, setC] = useState(cfg)
  const [dirty, setDirty] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [selectedId, setSelectedId] = useState(null) // null = la liste « Le parc »

  // Re-sync quand cfg change ailleurs — JAMAIS en écrasant une saisie non
  // sauvée (même garde que LogementsPanel/ParamsPanel).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (dirty) return; setC(cfg) }, [cfg])

  // flat enrichi : une entrée par logement plat + une par chambre feuille.
  const flat = useMemo(() => {
    const out = []
    ;(c.props || []).forEach((prop, pi) => {
      ;(prop.logs || []).forEach((log, li) => {
        out.push({ entity: log, prop, propIdx: pi, logIdx: li, isChambre: false, parentLog: null, chambreIdx: null })
        ;(log.chambres || []).forEach((ch, ci) => {
          out.push({ entity: ch, prop, propIdx: pi, logIdx: li, chambreIdx: ci, isChambre: true, parentLog: log })
        })
      })
    })
    return out
  }, [c])

  const selected = useMemo(
    () => flat.find((f) => f.entity.id === selectedId) || null,
    [flat, selectedId])

  // Filet ADR-197 : une sélection sur un parent sous-loc (ou un id disparu
  // après suppression) revient à la liste — jamais une fiche non éditable.
  useEffect(() => {
    if (!selectedId) return
    const target = flat.find((f) => f.entity.id === selectedId)
    const isParentSousLoc = target && !target.isChambre && hasChambres(target.entity)
    if (target && !isParentSousLoc) return
    setSelectedId(null)
  }, [selectedId, flat])

  // ── Proprios ──────────────────────────────────────────────────────
  const updateProp = useCallback((propIdx, field, value) => {
    setC((prev) => ({
      ...prev,
      props: (prev.props || []).map((p, i) => (i !== propIdx ? p : { ...p, [field]: value })),
    }))
    setDirty(true); setSavedAt(null)
  }, [])

  // ⚠️ UN IDENTIFIANT NE SE RÉUTILISE JAMAIS (lot « travail perdu »).
  // Retirer un logement laisse ses relevés, sa facturation et ses incidents en
  // place sous son identifiant : un logement neuf qui le reprendrait en
  // hériterait. Les identifiants retirés sont donc gardés dans le cfg
  // (`ids_logements_retires`, écrit au 💾 avec le retrait) et comptent dans le
  // max. Miroir serveur : lib/logement-creer-handlers.js::nextLogId.
  function numsRetires(c, re) {
    return (Array.isArray(c?.ids_logements_retires) ? c.ids_logements_retires : []).map((id) => {
      const m = String(id || '').match(re)
      return m ? parseInt(m[1], 10) : 0
    })
  }
  function nextLogId(prev) {
    const nums = (prev.props || []).flatMap((p) => (p.logs || []).map((l) => {
      const m = String(l.id || '').match(/^l(\d+)$/)
      return m ? parseInt(m[1], 10) : 0
    }))
    return 'l' + (Math.max(0, ...nums, ...numsRetires(prev, /^l(\d+)$/)) + 1)
  }
  function nextChambreId(prev) {
    const nums = (prev.props || []).flatMap((p) => (p.logs || []).flatMap((l) => (l.chambres || []).map((ch) => {
      const m = String(ch.id || '').match(/^lc(\d+)$/)
      return m ? parseInt(m[1], 10) : 0
    })))
    return 'lc' + (Math.max(0, ...nums, ...numsRetires(prev, /^lc(\d+)$/)) + 1)
  }

  // ── Créer / retirer un propriétaire (incident 30/09) ──────────────
  // L'ancien `addProp` poussait une entrée VIDE dans le brouillon au clic,
  // sans rien ouvrir ; la liste n'affichant que des logements, le clic
  // semblait sans effet, et chaque reclic ajoutait une fiche vide de plus.
  // Désormais : le formulaire valide la saisie (nom obligatoire), puis UNE
  // écriture par le chemin existant (`save` = saveCfg → /api/cfg, jeton de
  // version), sous verrou — un double clic n'écrit qu'une fois.
  const ecritureEnCours = useRef(false)

  const enregistrerMaintenant = useCallback(async (next) => {
    const cToSave = {
      ...next,
      props: (next.props || []).map((p) => {
        const computed = propDisplayName(p)
        return computed ? { ...p, nom: computed } : p
      }),
    }
    const avertissement = await save(cToSave)
    setC(cToSave)
    setDirty(false)
    setSavedAt(new Date())
    setTimeout(() => setSavedAt(null), 4000)
    return avertissement
  }, [save])

  const creerProprio = useCallback(async (saisie) => {
    if (ecritureEnCours.current) return { ok: false, enCours: true, error: 'Enregistrement déjà en cours.' }
    const id = nextPropId(c.props || [])
    const construit = construireProprio(id, saisie)
    if (!construit.ok) return { ok: false, error: construit.raison }
    ecritureEnCours.current = true
    try {
      const avertissement = await enregistrerMaintenant({
        ...c, props: [...(c.props || []), construit.entree],
      })
      return { ok: true, id, avertissement }
    } catch (e) {
      console.error('[logements-v2] creerProprio', e)
      return { ok: false, error: e.message || 'erreur serveur' }
    } finally {
      ecritureEnCours.current = false
    }
  }, [c, enregistrerMaintenant])

  // Retrait d'un propriétaire SANS logement ni historique — par IDENTIFIANT,
  // jamais par position, et la garde est RE-VÉRIFIÉE ici (l'écran peut avoir
  // un temps de retard sur le brouillon).
  const retirerProprio = useCallback(async (propId, contexte) => {
    if (ecritureEnCours.current) return { ok: false, enCours: true, error: 'Enregistrement déjà en cours.' }
    const prop = (c.props || []).find((p) => p.id === propId)
    if (!prop) return { ok: false, error: 'Propriétaire introuvable.' }
    const raisons = raisonsNonSupprimable(prop, contexte)
    if (raisons.length) return { ok: false, error: `Impossible de le retirer : ${raisons.join(', ')}.` }
    ecritureEnCours.current = true
    try {
      const avertissement = await enregistrerMaintenant({
        ...c, props: (c.props || []).filter((p) => p.id !== propId),
      })
      return { ok: true, avertissement }
    } catch (e) {
      console.error('[logements-v2] retirerProprio', e)
      return { ok: false, error: e.message || 'erreur serveur' }
    } finally {
      ecritureEnCours.current = false
    }
  }, [c, enregistrerMaintenant])

  const removePropConfirmed = useCallback((propIdx) => {
    setC((prev) => ({ ...prev, props: (prev.props || []).filter((_, i) => i !== propIdx) }))
    setDirty(true); setSavedAt(null)
  }, [])

  const renameProp = useCallback((propIdx, rawText) => {
    const trimmed = String(rawText || '').trim()
    if (!trimmed) return
    const parts = trimmed.split(/\s+/)
    const nom_famille = parts[0]
    const prenom = parts.slice(1).join(' ')
    setC((prev) => ({
      ...prev,
      props: (prev.props || []).map((p, i) => (i !== propIdx ? p : { ...p, nom_famille, prenom })),
    }))
    setDirty(true); setSavedAt(null)
  }, [])

  // ── Logements ─────────────────────────────────────────────────────
  const addLog = useCallback((propIdx) => {
    let newId = null
    setC((prev) => {
      newId = nextLogId(prev)
      return {
        ...prev,
        props: (prev.props || []).map((p, i) => (i !== propIdx ? p : {
          ...p,
          logs: [...p.logs, { id: newId, nom: 'Nouveau logement', taux: 20, ical: '' }],
        })),
      }
    })
    setDirty(true); setSavedAt(null)
    if (newId) setSelectedId(newId)
    return newId
  }, [])

  // La sous-location FBZ = une nouvelle CHAMBRE du parent p6 (ADR-197) — c'est
  // ce qui lui fait hériter la logique bénéfice (CA − loyer/charges du parent).
  const sousLocTarget = useMemo(() => {
    const propIdx = (c.props || []).findIndex((p) => p.id === 'p6')
    if (propIdx < 0) return null
    const parentIdx = (c.props[propIdx].logs || []).findIndex(hasChambres)
    if (parentIdx < 0) return null
    return { propIdx, parentIdx }
  }, [c])

  const addSousLoc = useCallback(() => {
    if (!sousLocTarget) return null
    const { propIdx, parentIdx } = sousLocTarget
    let newId = null
    setC((prev) => {
      newId = nextChambreId(prev)
      return {
        ...prev,
        props: (prev.props || []).map((p, i) => (i !== propIdx ? p : {
          ...p,
          logs: p.logs.map((l, j) => (j !== parentIdx ? l : {
            ...l,
            chambres: [...(l.chambres || []), { id: newId, nom: 'Nouvelle sous-location' }],
          })),
        })),
      }
    })
    setDirty(true); setSavedAt(null)
    if (newId) setSelectedId(newId)
    return newId
  }, [sousLocTarget])

  const renameLog = useCallback((propIdx, logIdx, newName, chambreIdx = null) => {
    const trimmed = String(newName || '').trim()
    if (!trimmed) return
    setC((prev) => ({
      ...prev,
      props: (prev.props || []).map((p, i) => (i !== propIdx ? p : {
        ...p,
        logs: p.logs.map((l, j) => {
          if (j !== logIdx) return l
          if (chambreIdx !== null) {
            return {
              ...l,
              chambres: (l.chambres || []).map((ch, ci) => (ci !== chambreIdx ? ch : { ...ch, nom: trimmed })),
            }
          }
          return { ...l, nom: trimmed }
        }),
      })),
    }))
    setDirty(true); setSavedAt(null)
  }, [])

  // Suppression — retrait du logement (et de ses chambres) du BROUILLON
  // seulement ; rien n'est écrit avant 💾 (handleSave).
  //
  // ⚠️ LOT « TRAVAIL PERDU » — la cascade a été RETIRÉE, pas différée.
  // Avant, la confirmation effaçait IMMÉDIATEMENT (avant 💾) :
  //   · les relevés du logement dans fbz8-data (saveData) ;
  //   · ses lignes du journal de facturation (saveLog), filtrées aussi par
  //     NOM — un logement homonyme perdait les siennes ;
  //   · ses lignes reservations / logements_beds24_mapping / incidents en base
  //     (les deux premières sans droit de suppression côté client, donc sans
  //     effet ; incidents a une règle admin « tout », la suppression pouvait
  //     donc réellement partir).
  // Fermer l'onglet sans 💾 rendait le logement… sans son historique.
  // Consigne Dimitri (dette « supprimer un logement ») : NE PAS étendre la
  // suppression ; relevés, facturation et incidents sont de l'historique à
  // garder (archiver, jamais effacer). Les différer à 💾 aurait gardé la
  // destruction : on les retire. Le seul effet immédiat restant est local
  // (cache de correspondance Beds24, relu à la demande).
  const removeLogConfirmed = useCallback((propIdx, logIdx) => {
    const log = c.props[propIdx]?.logs[logIdx]
    if (!log) return
    const lid = log.id
    if (!lid) return
    const idsRetires = [lid, ...(log.chambres || []).map((ch) => ch.id)]

    const dejaRetires = Array.isArray(c.ids_logements_retires) ? c.ids_logements_retires : []
    const newC = {
      ...c,
      props: (c.props || []).map((p, i) => (i !== propIdx ? p : {
        ...p,
        logs: p.logs.filter((l) => l.id !== lid),
      })),
      // Partent au 💾 AVEC le retrait (même écriture) : jamais réattribués.
      ids_logements_retires: [...new Set([...dejaRetires, ...idsRetires])],
    }
    setC(newC)
    clearMappingCache(idsRetires)

    setDirty(true); setSavedAt(null)
    if (idsRetires.includes(selectedId)) setSelectedId(null)
  }, [c, selectedId])

  // ── Save (recalcule prop.nom dérivé avant persist) ────────────────
  const handleSave = useCallback(async () => {
    if (!dirty) return
    const cToSave = {
      ...c,
      props: (c.props || []).map((p) => {
        const computed = propDisplayName(p)
        return computed ? { ...p, nom: computed } : p
      }),
    }
    try {
      const avertissement = await save(cToSave)
      setC(cToSave)
      setDirty(false)
      setSavedAt(new Date())
      setTimeout(() => setSavedAt(null), 4000)
      if (avertissement) alert(avertissement)
    } catch (e) {
      console.error('[logements-v2] save', e)
      alert("Échec de l'enregistrement : " + (e.message || 'erreur serveur'))
    }
  }, [c, dirty, save])

  // ── Éditions de l'entité sélectionnée ─────────────────────────────
  const patchEntity = useCallback((transform) => {
    if (!selected) return
    setC((prev) => ({
      ...prev,
      props: (prev.props || []).map((p, i) => (i !== selected.propIdx ? p : {
        ...p,
        logs: p.logs.map((l, j) => {
          if (j !== selected.logIdx) return l
          if (selected.isChambre) {
            return {
              ...l,
              chambres: (l.chambres || []).map((ch, ci) => (ci !== selected.chambreIdx ? ch : transform(ch))),
            }
          }
          return transform(l)
        }),
      })),
    }))
  }, [selected])

  const updateLog = useCallback((field, value) => {
    if (!selected) return
    patchEntity((e) => ({ ...e, [field]: value }))
    setDirty(true); setSavedAt(null)
  }, [selected, patchEntity])

  const updateLogBatch = useCallback((patch) => {
    if (!selected || !patch || typeof patch !== 'object') return
    patchEntity((e) => ({ ...e, ...patch }))
    setDirty(true); setSavedAt(null)
  }, [selected, patchEntity])

  // Patch + persist IMMÉDIAT (barème taxe, ville — arbitrage Dimitri LOT TAXE
  // 0.4) : cfg suivant construit en synchrone, jamais de handleSave racé.
  const applyPatchAndSaveNow = useCallback(async (patch) => {
    if (!selected || !patch || typeof patch !== 'object') {
      return { ok: false, error: 'Aucun logement sélectionné' }
    }
    const next = {
      ...c,
      props: (c.props || []).map((p, i) => (i !== selected.propIdx ? p : {
        ...p,
        logs: p.logs.map((l, j) => {
          if (j !== selected.logIdx) return l
          if (selected.isChambre) {
            return {
              ...l,
              chambres: (l.chambres || []).map((ch, ci) => (ci !== selected.chambreIdx ? ch : { ...ch, ...patch })),
            }
          }
          return { ...l, ...patch }
        }),
      })),
    }
    const cToSave = {
      ...next,
      props: (next.props || []).map((p) => {
        const computed = propDisplayName(p)
        return computed ? { ...p, nom: computed } : p
      }),
    }
    try {
      await save(cToSave)
      setC(cToSave)
      setDirty(false)
      setSavedAt(new Date())
      setTimeout(() => setSavedAt(null), 4000)
      return { ok: true }
    } catch (e) {
      console.error('[logements-v2] applyPatchAndSaveNow', e)
      return { ok: false, error: e.message || 'erreur serveur' }
    }
  }, [c, selected, save])

  // « Copier vers les autres chambres » (WAGON R5 lot C / ADR-197 lot 3).
  const copyToChambres = useCallback((sectionId) => {
    if (!selected || !selected.isChambre) return
    const parentLog = selected.parentLog
    const chambres = (parentLog && Array.isArray(parentLog.chambres)) ? parentLog.chambres : []
    const sourceLog = chambres[selected.chambreIdx]
    const sisters = chambres.filter((_, i) => i !== selected.chambreIdx)
    if (!sourceLog || sisters.length === 0) return
    const plan = buildCopyPlan(sourceLog, sisters, sectionId)
    if (!plan.perChambre.some((ch) => !ch.alreadyUpToDate)) return
    const patchedById = new Map(applyCopyPlan(sisters, plan).map((s) => [s.id, s]))
    setC((prev) => ({
      ...prev,
      props: (prev.props || []).map((p, i) => (i !== selected.propIdx ? p : {
        ...p,
        logs: p.logs.map((l, j) => (j !== selected.logIdx ? l : {
          ...l,
          chambres: (l.chambres || []).map((ch) => patchedById.get(ch.id) || ch),
        })),
      })),
    }))
    setDirty(true); setSavedAt(null)
  }, [selected])

  // Paliers + investissement : early-return sur chambre (commission au parent).
  const updateLogPaliers = useCallback((idx, field, value) => {
    if (!selected || selected.isChambre) return
    patchEntity((e) => {
      const newPaliers = [...(e.paliers || [])]
      newPaliers[idx] = { ...newPaliers[idx], [field]: value }
      return { ...e, paliers: newPaliers }
    })
    setDirty(true)
  }, [selected, patchEntity])

  const addPalier = useCallback(() => {
    if (!selected || selected.isChambre) return
    patchEntity((e) => ({ ...e, paliers: [...(e.paliers || []), { maxNuits: 7, taux: 20 }] }))
    setDirty(true)
  }, [selected, patchEntity])

  const removePalier = useCallback((idx) => {
    if (!selected || selected.isChambre) return
    patchEntity((e) => ({ ...e, paliers: (e.paliers || []).filter((_, x) => x !== idx) }))
    setDirty(true)
  }, [selected, patchEntity])

  const addInvest = useCallback((label, montant) => {
    if (!selected || selected.isChambre || !label || !montant) return
    patchEntity((e) => ({
      ...e,
      investissement: [...(e.investissement || []), { id: 'inv' + Date.now().toString(36), label, montant: Number(montant) }],
    }))
    setDirty(true)
  }, [selected, patchEntity])

  const removeInvest = useCallback((invId) => {
    if (!selected || selected.isChambre) return
    patchEntity((e) => ({ ...e, investissement: (e.investissement || []).filter((inv) => inv.id !== invId) }))
    setDirty(true)
  }, [selected, patchEntity])

  return {
    c, dirty, savedAt, flat, selected, selectedId, setSelectedId,
    sousLocTarget,
    handleSave, applyPatchAndSaveNow,
    updateLog, updateLogBatch, updateProp, renameProp, renameLog,
    creerProprio, retirerProprio, addLog, addSousLoc, removePropConfirmed, removeLogConfirmed,
    copyToChambres,
    updateLogPaliers, addPalier, removePalier, addInvest, removeInvest,
  }
}
